<template>
    <div class="content container-fluid">
        <div class="page-header">
            <div class="row align-items-end">
                <div class="col-sm mb-2 mb-sm-0">
                    <nav aria-label="breadcrumb">
                        <ol class="breadcrumb breadcrumb-no-gutter">
                            <li class="breadcrumb-item"><router-link class="breadcrumb-link" to="/">Kokpit</router-link></li>
                            <li class="breadcrumb-item active" aria-current="page">Wysłane SMS</li>
                        </ol>
                    </nav>
                    <h1 class="page-header-title">Wysłane SMS</h1>
                </div>
            </div>
        </div>

        <div class="card shadow-sm border-0 mb-3">
            <div class="card-body">
                <div class="row g-3 align-items-end">
                    <div class="col-md-4">
                        <label class="form-label">Od daty</label>
                        <date-picker
                            v-model="dateFrom"
                            type="date"
                            format="YYYY-MM-DD"
                            value-type="YYYY-MM-DD"
                            :editable="false"
                            input-class="form-control"
                            placeholder="Od"
                        />
                    </div>
                    <div class="col-md-4">
                        <label class="form-label">Do daty</label>
                        <date-picker
                            v-model="dateTo"
                            type="date"
                            format="YYYY-MM-DD"
                            value-type="YYYY-MM-DD"
                            :editable="false"
                            input-class="form-control"
                            placeholder="Do"
                        />
                    </div>
                    <div class="col-md-4">
                        <button class="btn btn-primary me-2" @click="load" :disabled="loading">
                            <i class="bi-search me-1"></i> Filtruj
                        </button>
                        <button class="btn btn-white" @click="clearFilters" :disabled="loading">
                            Wyczyść
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div class="card shadow-sm border-0">
            <div class="card-header border-bottom-0 d-flex justify-content-between align-items-center">
                <h4 class="card-header-title">Lista wiadomości</h4>
                <span v-if="testMode" class="badge bg-soft-warning text-warning">Tryb testowy aktywny</span>
            </div>

            <div class="table-responsive">
                <table class="table table-lg table-borderless table-thead-bordered table-nowrap table-align-middle card-table">
                    <thead class="thead-light">
                        <tr>
                            <th style="width: 50px;">#</th>
                            <th>Data</th>
                            <th>Klient</th>
                            <th>Odbiorca</th>
                            <th>Treść</th>
                            <th class="text-end">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(item, index) in items" :key="item.sms_id || index">
                            <td>{{ index + 1 }}</td>
                            <td>
                                <div class="d-flex flex-column">
                                    <span class="text-dark fw-bold">{{ formatDate(item.created_at) }}</span>
                                    <small class="text-muted">{{ formatTime(item.created_at) }}</small>
                                </div>
                            </td>
                            <td><span class="badge bg-soft-secondary text-secondary">{{ item.client_id }}</span></td>
                            <td>
                                <span class="badge bg-soft-secondary text-secondary">
                                    <i class="bi-phone me-1"></i> {{ isAdmin ? item.sms_to : $maskPhone(item.sms_to) }}
                                </span>
                            </td>
                            <td>
                                <div class="text-wrap" style="max-width: 400px;">
                                    {{ isAdmin ? item.sms_body : $maskSms(item.sms_body) }}
                                </div>
                            </td>
                            <td class="text-end">
                                <span v-if="item.status === 'sent'" class="badge bg-soft-success text-success rounded-pill">Wysłano</span>
                                <span v-else-if="item.status === 'test'" class="badge bg-soft-info text-info rounded-pill">Test</span>
                                <span v-else-if="item.status === 'failed' || item.status === 'error'" class="badge bg-soft-danger text-danger rounded-pill">Błąd</span>
                                <span v-else class="badge bg-soft-warning text-warning rounded-pill">{{ item.status || 'Oczekiwanie' }}</span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div v-if="items.length === 0 && !loading" class="card-body text-center py-5">
                <i class="bi-chat-dots display-1 text-muted opacity-25"></i>
                <p class="mt-3">Brak wiadomości w wybranym okresie.</p>
            </div>

            <div v-if="loading" class="card-body text-center py-5">
                <div class="spinner-border text-primary" role="status"></div>
            </div>
        </div>
    </div>
</template>

<script>
import panelApi from '../../api/panel-axios.js';
import appConfig from '../../config.js';
import dayjs from 'dayjs';
import DatePicker from 'vue2-datepicker';

export default {
    name: 'SmsIndex',
    components: { DatePicker },
    data() {
        return {
            items: [],
            loading: false,
            dateFrom: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
            dateTo: dayjs().format('YYYY-MM-DD'),
        };
    },
    computed: {
        isAdmin() {
            return ['owner', 'manager'].includes(this.$store.getters['app/role']);
        },
        testMode() {
            return this.$store.getters['app/options']?.sms_test_mode === true;
        },
    },
    methods: {
        load() {
            this.loading = true;
            const params = {};

            if (this.dateFrom) {
                params.date_from = this.dateFrom;
            }
            if (this.dateTo) {
                params.date_to = this.dateTo;
            }

            panelApi.get(appConfig.panelApiPrefix + '/sms/messages', { params }).then(response => {
                this.items = response.data.data || [];
            }).catch(err => {
                console.error(err);
            }).finally(() => {
                this.loading = false;
            });
        },
        clearFilters() {
            this.dateFrom = dayjs().subtract(7, 'day').format('YYYY-MM-DD');
            this.dateTo = dayjs().format('YYYY-MM-DD');
            this.load();
        },
        formatDate(value) {
            const dateStr = value?.date ?? value;
            return dateStr ? dayjs(dateStr).format('YYYY-MM-DD') : '';
        },
        formatTime(value) {
            const dateStr = value?.date ?? value;
            return dateStr ? dayjs(dateStr).format('HH:mm:ss') : '';
        },
    },
    mounted() {
        this.load();
    },
};
</script>

<style scoped>
.text-wrap {
    white-space: normal !important;
    word-break: break-all;
}

.bg-soft-success {
    background-color: rgba(0, 201, 167, 0.1) !important;
}

.bg-soft-danger {
    background-color: rgba(237, 76, 120, 0.1) !important;
}

.bg-soft-warning {
    background-color: rgba(245, 202, 153, 0.1) !important;
}

.bg-soft-info {
    background-color: rgba(55, 125, 255, 0.1) !important;
}

.bg-soft-secondary {
    background-color: rgba(113, 119, 125, 0.1) !important;
}
</style>
